/**
 * As partes do cartão "Rever leitura na fonte" (E155.F3, GUS-192, ADR-0105 §2.7): o campo como o
 * exame o usa e a fonte dele, a leitura proposta com a fonte e o que a releitura moveu, as
 * candidatas da releitura ambígua, o motivo, o aviso e a recusa do servidor.
 */
import { Button, Field, Textarea } from '@/components/ui';
import { paginaDaFonte, type CampoDaFichaData, type PaginaNaFonte } from '@/lib/ficha_da_leitura';
import {
  MOTIVO_MAXIMO,
  MOTIVO_MINIMO,
  RELEITURAS_MAX_POR_CAMPO,
  paginaDaFonteDaReleitura,
  type CandidataDaReleituraData,
  type FonteDaReleituraData,
  type RecusaDaReleitura,
  type ReleituraOutData,
} from '@/lib/releitura_da_leitura';

import { preencher, r, rotuloDoCampo, t, textoDaFonte } from './textos';

export type VerNaFonte = (alvo: PaginaNaFonte) => void;

function BotaoDaFonte({ alvo, onVer }: { alvo: PaginaNaFonte | null; onVer: VerNaFonte }) {
  if (alvo === null) return null;
  return (
    <Button type="button" variant="link" size="sm" onClick={() => onVer(alvo)}>
      {t.verNaFonte}
    </Button>
  );
}

function Termo({ rotulo, children }: { rotulo: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <dt className="text-caption text-fg-dim">{rotulo}</dt>
      <dd className="text-fg">{children}</dd>
    </div>
  );
}

/** O valor que o exame usa hoje, ou a situação nomeada. */
function leituraAtual(campo: CampoDaFichaData): string {
  if (campo.situacao !== 'lido') return t.situacao[campo.situacao];
  return campo.literal ?? campo.valor ?? r.semLeituraAtual;
}

/** Campo / Leitura atual / Fonte, e os detalhes técnicos por expansão. */
export function CampoAtual({
  campo,
  versao,
  onVerNaFonte,
}: {
  campo: CampoDaFichaData;
  versao: number;
  onVerNaFonte: VerNaFonte;
}): JSX.Element {
  const fonte = campo.fontes[0] ?? campo.divergencias[0] ?? null;
  return (
    <>
      <dl className="grid gap-sc-2 sm:grid-cols-3" data-testid="releitura-atual">
        <Termo rotulo={r.campo}>
          <span className="font-semibold">{rotuloDoCampo(campo)}</span>
        </Termo>
        <Termo rotulo={r.leituraAtual}>
          <span data-testid="releitura-leitura-atual">{leituraAtual(campo)}</span>
        </Termo>
        <Termo rotulo={r.fonte}>
          {fonte === null ? (
            <span className="text-fg-muted">{r.semFonte}</span>
          ) : (
            <span data-testid="releitura-fonte-atual">
              {textoDaFonte(fonte)}{' '}
              <BotaoDaFonte alvo={paginaDaFonte(fonte)} onVer={onVerNaFonte} />
            </span>
          )}
        </Termo>
      </dl>
      <details className="text-body-sm" data-testid="releitura-detalhes">
        <summary className="cursor-pointer text-accent-700">{r.detalhes}</summary>
        <p className="mt-1 text-fg-muted">
          {fonte === null ? r.semLocalizador : preencher(r.localizador, { fonte: fonte.trecho })}
        </p>
        <p className="text-fg-muted">{preencher(r.versaoDaFicha, { versao })}</p>
        <p className="font-semibold text-fg">{r.ressalvaProposta}</p>
      </details>
    </>
  );
}

function FonteProposta({
  fonte,
  onVerNaFonte,
}: {
  fonte: FonteDaReleituraData;
  onVerNaFonte: VerNaFonte;
}): JSX.Element {
  return (
    <span className="space-y-0.5" data-testid="releitura-fonte">
      <span className="block">
        {textoDaFonte(fonte)}
        {fonte.origem_do_texto === 'imagem' ? ` · ${t.origemImagem}` : ''}{' '}
        <BotaoDaFonte alvo={paginaDaFonteDaReleitura(fonte)} onVer={onVerNaFonte} />
      </span>
      {fonte.rotulo !== null ? (
        <span className="block text-body-sm text-fg-muted">{fonte.rotulo}</span>
      ) : null}
      <span className="block text-body-sm text-fg-muted" data-testid="releitura-localizado">
        {fonte.localizado ? r.localizado : r.naoLocalizado}
      </span>
    </span>
  );
}

function Candidatas({
  candidatas,
  onVerNaFonte,
  onUsar,
}: {
  candidatas: CandidataDaReleituraData[];
  onVerNaFonte: VerNaFonte;
  onUsar: (candidata: CandidataDaReleituraData) => void;
}): JSX.Element {
  return (
    <div>
      <p className="text-caption text-fg-dim">{r.candidatas}</p>
      <ul className="mt-1 space-y-sc-2">
        {candidatas.map((candidata) => (
          <li
            key={`${candidata.fonte.documento_id}-${candidata.fonte.pagina}-${candidata.valor}`}
            className="flex flex-wrap items-baseline gap-x-sc-2"
            data-testid="releitura-candidata"
          >
            <span className="font-semibold text-fg">{candidata.literal ?? candidata.valor}</span>
            <span className="text-body-sm text-fg-muted">{textoDaFonte(candidata.fonte)}</span>
            <BotaoDaFonte alvo={paginaDaFonteDaReleitura(candidata.fonte)} onVer={onVerNaFonte} />
            <Button type="button" size="sm" variant="secondary" onClick={() => onUsar(candidata)}>
              {r.usarCandidata}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** O que a releitura moveu: as peças verificadas de novo e as concluídas reabertas. */
function Movimento({ resposta }: { resposta: ReleituraOutData }): JSX.Element | null {
  if (resposta.releitura.resultado !== 'mudou') return null;
  const reabertas = resposta.releitura.conteudo.pecas_reabertas.length;
  return (
    <p className="text-body-sm text-fg-muted" data-testid="releitura-movimento">
      {preencher(r.reverificadas, { n: resposta.reverificadas })}
      {reabertas > 0 ? ` ${preencher(r.pecasReabertas, { n: reabertas })}` : ''}
    </p>
  );
}

/** A leitura proposta: o resultado, o valor com a fonte, as candidatas e o que moveu. */
export function LeituraProposta({
  resposta,
  onVerNaFonte,
  onUsarCandidata,
}: {
  resposta: ReleituraOutData;
  onVerNaFonte: VerNaFonte;
  onUsarCandidata: (candidata: CandidataDaReleituraData) => void;
}): JSX.Element {
  const { releitura } = resposta;
  const conteudo = releitura.conteudo;
  return (
    <div
      className="space-y-sc-2 border border-border bg-surface p-sc-3"
      data-testid="releitura-proposta"
      data-resultado={releitura.resultado}
    >
      <p className="text-body-sm font-semibold text-fg">{r.resultado[releitura.resultado]}</p>
      {conteudo.depois !== null ? (
        <dl className="grid gap-sc-2 sm:grid-cols-2">
          <Termo rotulo={r.leituraProposta}>
            <span className="font-semibold" data-testid="releitura-valor">
              {conteudo.literal ?? conteudo.depois}
            </span>
          </Termo>
          <Termo rotulo={r.fonte}>
            {conteudo.fonte === null ? (
              <span className="text-fg-muted">{r.semFonte}</span>
            ) : (
              <FonteProposta fonte={conteudo.fonte} onVerNaFonte={onVerNaFonte} />
            )}
          </Termo>
        </dl>
      ) : null}
      {conteudo.candidatas.length > 0 ? (
        <Candidatas
          candidatas={conteudo.candidatas}
          onVerNaFonte={onVerNaFonte}
          onUsar={onUsarCandidata}
        />
      ) : null}
      <Movimento resposta={resposta} />
    </div>
  );
}

export function MotivoDaReleitura({
  motivo,
  onChange,
  testId,
}: {
  motivo: string;
  onChange: (motivo: string) => void;
  testId: string;
}): JSX.Element {
  return (
    <Field
      label={r.motivoRotulo}
      description={preencher(r.motivoAjuda, { n: MOTIVO_MINIMO })}
      required
    >
      <Textarea
        value={motivo}
        onChange={(evento) => onChange(evento.target.value)}
        rows={3}
        maxLength={MOTIVO_MAXIMO}
        data-testid={testId}
      />
    </Field>
  );
}

export function AvisoDaReleitura(): JSX.Element {
  return (
    <div
      role="status"
      className="border border-accent-200 bg-accent-bg p-sc-3"
      data-testid="releitura-feita"
    >
      <p className="text-body-sm font-semibold text-fg">{r.feita}</p>
      <p className="text-body-sm text-fg-muted">{r.feitaTexto}</p>
    </div>
  );
}

export function Recusa({ recusa }: { recusa: RecusaDaReleitura | null }): JSX.Element | null {
  if (recusa === null) return null;
  const n = recusa === 'esgotada' ? RELEITURAS_MAX_POR_CAMPO : MOTIVO_MINIMO;
  return (
    <p role="alert" className="text-body-sm text-danger-700" data-testid="releitura-recusa">
      {preencher(r.recusa[recusa], { n })}
    </p>
  );
}
